/**
 * Get the maximum number of days for a given month and year
 */
export function getDaysInMonth(month: number, year: number): number {
  const daysInMonth: Record<number, number> = {
    1: 31, // January
    2: isLeapYear(year) ? 29 : 28, // February (depends on leap year)
    3: 31, // March
    4: 30, // April
    5: 31, // May
    6: 30, // June
    7: 31, // July
    8: 31, // August
    9: 30, // September
    10: 31, // October
    11: 30, // November
    12: 31, // December
  }

  return daysInMonth[month] || 31
}

/**
 * Check if a year is a leap year
 */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

/**
 * Generate day options for a given month and year
 */
export function generateDayOptions(
  month: string,
  year: string,
): Array<{ value: string; label: string }> {
  if (!month) {
    // If no month selected, show all 31 days
    return Array.from({ length: 31 }, (_, i) => ({
      value: String(i + 1).padStart(2, '0'),
      label: String(i + 1).padStart(2, '0'),
    }))
  }

  const monthNum = parseInt(month, 10)
  // Use provided year or default to current year for leap year calculation
  const yearNum = year ? parseInt(year, 10) : new Date().getFullYear()
  const maxDays = getDaysInMonth(monthNum, yearNum)

  return Array.from({ length: maxDays }, (_, i) => ({
    value: String(i + 1).padStart(2, '0'),
    label: String(i + 1).padStart(2, '0'),
  }))
}

/**
 * Validate if a date is valid
 */
export function isValidDate(day: string, month: string, year: string): boolean {
  if (!day || !month || !year) return false

  const dayNum = parseInt(day, 10)
  const monthNum = parseInt(month, 10)
  const yearNum = parseInt(year, 10)

  if (monthNum < 1 || monthNum > 12) return false
  if (yearNum < 1900 || yearNum > new Date().getFullYear()) return false

  const maxDays = getDaysInMonth(monthNum, yearNum)
  return dayNum >= 1 && dayNum <= maxDays
}

/**
 * Check whether a timestamp is over 10 days old
 */
export function isOverTenDaysAgo(timestamp?: string | number | Date | null): boolean {
  if (!timestamp) return false
  const date = new Date(timestamp)
  if (Number.isNaN(date.getTime())) return false
  const diffMs = Date.now() - date.getTime()
  const tenDaysMs = 10 * 24 * 60 * 60 * 1000
  return diffMs > tenDaysMs
}
